import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as microsoftTeams from "@microsoft/teams-js";
import { Flex, Button, Input, Datepicker, Tooltip } from "@fluentui/react-northstar";
import { OpenOutsideIcon } from "@fluentui/react-icons-northstar";
import find from "lodash/find";
import { useAuth } from "../../contexts/authContext";
import { useApp } from "../../contexts/appContext";
import styles from "./commonModal.module.scss";

import IProject from "../../interfaces/IProject";
import IUpdateProject from "../../interfaces/IUpdateProject";
import { mergeUpdatedProject, updateProject } from "../../services/dataService";
import { useProjects } from "../../queries";
import { numberToString, stringToNumber } from "../../common/numberOperations";
import { parseDateToDate } from "../../common/dateOperations";
import { hasValue } from "../../common/textOperations";

const EditProject = () => {
  const [date, setDate] = useState<Date>();
  const [hours, setHours] = useState("0");
  const [processing, setProcessing] = useState(false);
  const { id } = useParams<{ id: string }>();
  const { state: authState } = useAuth();
  const { accessToken } = authState;
  const { state: appState } = useApp();
  const { isDarkTheme } = appState;
  const { data: projects } = useProjects();

  useEffect(() => {
    if (!id) { return; }
    const project = find(projects, (x: IProject) => x.id === id);
    if (project) {
      setDate(project.dueDate ? parseDateToDate(project.dueDate) : new Date());
      setHours(numberToString(project.remainingHours));
    }
  }, [id, projects]);

  const dateFormatter = (value: Date) => {
    const day = value.getDate();
    const month = value.getMonth() + 1;
    const year = value.getFullYear();
    return `${day}. ${month}. ${year}`;
  };

  const update = async () => {
    if (!accessToken) { return; }

    setProcessing(true);
    const data: IUpdateProject = { dueDate: date, remainingHours: stringToNumber(hours) };
    try {
      await updateProject(accessToken, id, data, false);
      microsoftTeams.dialog.url.submit(mergeUpdatedProject(projects ?? [], id, data));
    } catch (error) {
      console.log(error);
      setProcessing(false);
    }
  };

  const dateChanged = (_: any, data: any) => {
    setDate(data.value ?? new Date());
  };

  const hoursChanged = (_: any, data: any) => {
    setHours(data.value);
  };

  const projectLink = () => {
    return import.meta.env.VITE_CRM_BASE_URL + `&pagetype=entityrecord&etn=incident&id=${id}`;
  };

  const formIsValid = () => {
    return hasValue(date) && hasValue(hours) && stringToNumber(hours) > 0;
  };

  return (
    <Flex gap="gap.medium" column className={styles.container} style={{ backgroundColor: isDarkTheme ? "#292929" : "#FFFFFF" }}>

      <Flex column gap="gap.smaller">
        <small>Termín splnění</small>
        {date &&
          <Datepicker
            selectedDate={date}
            defaultSelectedDate={date}
            formatMonthDayYear={dateFormatter}
            onDateChange={dateChanged}
            inputOnly
          />
        }
      </Flex>

      <Flex column gap="gap.smaller">
        <small>Zbývá odpracovat (h)</small>
        <Input value={hours} onChange={hoursChanged} />
      </Flex>

      <Flex gap="gap.smaller">
        <Flex.Item grow>
          <Button content="Aktualizovat" primary loading={processing} disabled={!formIsValid() || processing} onClick={update} />
        </Flex.Item>
        <Tooltip
          trigger={<Button text iconOnly icon={<OpenOutsideIcon />} onClick={() => window.open(projectLink())} />}
          content="Otevřít případ v CRM"
        />
      </Flex>
    </Flex>
  );
};

export default EditProject;
